import React, { useRef, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import './BirthdayModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import html2canvas from 'html2canvas';

const BirthdayModal = ({ show, handleClose, nombresCumpleaneros }) => {
    const modalContentRef = useRef(null);
    const [descargando, setDescargando] = useState(false);
    
    // Frases motivadoras aleatorias
    const frases = [
        "Que este nuevo año de vida esté lleno de éxitos, alegría y momentos inolvidables. ¡Eres una parte valiosa de nuestro equipo!",
        "Tu dedicación y talento hacen de este equipo algo especial. ¡Que cumplas muchos más y que todos sean extraordinarios!",
        "Hoy celebramos no solo tu cumpleaños, sino también todo lo que aportas al equipo. ¡Feliz vuelta al sol!",
        "Que este año te traiga nuevas oportunidades, crecimiento profesional y personal, y mucha felicidad. ¡Feliz cumpleaños!",
        "Tu energía y positividad iluminan nuestro espacio de trabajo. ¡Que tengas un día tan especial como tú!",
        "Cada año que pasa es una nueva oportunidad para brillar aún más. ¡Feliz cumpleaños, que lo disfrutes al máximo!",
        "Eres una pieza fundamental en nuestro equipo. ¡Que este nuevo año de vida esté lleno de bendiciones y logros!"
    ];

    const fraseAleatoria = frases[Math.floor(Math.random() * frases.length)];

    // Obtener fecha actual formateada
    const fechaActual = new Date();
    const opciones = { day: 'numeric', month: 'long', year: 'numeric' };
    const fechaFormateada = fechaActual.toLocaleDateString('es-ES', opciones);
    const dia = fechaActual.getDate();
    const mes = fechaActual.toLocaleDateString('es-ES', { month: 'long' });

    const descargarImagen = async () => {
        if (!modalContentRef.current) return;

        setDescargando(true);

        try {
            // Guardar el estado original de animaciones y transformaciones
            const elementos = modalContentRef.current.querySelectorAll('*');
            const estilosOriginales = [];
            
            // Pausar todas las animaciones, transiciones y resetear transformaciones
            elementos.forEach((elemento, index) => {
                const computedStyle = window.getComputedStyle(elemento);
                estilosOriginales[index] = {
                    animation: elemento.style.animation,
                    transition: elemento.style.transition,
                    animationPlayState: elemento.style.animationPlayState,
                    transform: elemento.style.transform,
                    opacity: elemento.style.opacity
                };
                
                // Pausar animaciones
                elemento.style.animation = 'none';
                elemento.style.transition = 'none';
                elemento.style.animationPlayState = 'paused';
                
                // Asegurar que todo sea visible
                if (computedStyle.opacity === '0') {
                    elemento.style.opacity = '1';
                }
                
                // Resetear transformaciones que puedan ocultar elementos
                if (computedStyle.transform !== 'none') {
                    elemento.style.transform = 'none';
                }
            });

            // Forzar el repaint del navegador múltiples veces
            void modalContentRef.current.offsetHeight;
            void modalContentRef.current.offsetWidth;
            
            // Esperar más tiempo para asegurar que todo se renderice
            await new Promise(resolve => setTimeout(resolve, 500));

            // Capturar la imagen con configuración optimizada
            const canvas = await html2canvas(modalContentRef.current, {
                backgroundColor: '#e3f2fd',
                scale: 2,
                logging: false,
                useCORS: true,
                allowTaint: true,
                foreignObjectRendering: false,
                imageTimeout: 0,
                removeContainer: false,
                ignoreElements: () => false,
                onclone: (clonedDoc) => {
                    const clonedElement = clonedDoc.querySelector('.birthday-modal-body');
                    if (clonedElement) {
                        // Asegurar que todos los elementos sean visibles en el clon
                        const allElements = clonedElement.querySelectorAll('*');
                        allElements.forEach(el => {
                            el.style.animation = 'none';
                            el.style.transition = 'none';
                            el.style.animationPlayState = 'paused';
                            el.style.opacity = '1';
                            el.style.transform = 'none';
                            el.style.visibility = 'visible';
                            el.style.display = el.style.display === 'none' ? 'block' : el.style.display;
                        });
                    }
                }
            });

            // Restaurar estilos originales
            elementos.forEach((elemento, index) => {
                if (estilosOriginales[index]) {
                    elemento.style.animation = estilosOriginales[index].animation || '';
                    elemento.style.transition = estilosOriginales[index].transition || '';
                    elemento.style.animationPlayState = estilosOriginales[index].animationPlayState || '';
                    elemento.style.transform = estilosOriginales[index].transform || '';
                    elemento.style.opacity = estilosOriginales[index].opacity || '';
                }
            });

            // Crear y descargar el archivo
            const link = document.createElement('a');
            const fecha = new Date().toLocaleDateString('es-MX').replace(/\//g, '-');
            link.download = `Cumpleanos_${nombresCumpleaneros.replace(/\s+/g, '_')}_${fecha}.png`;
            link.href = canvas.toDataURL('image/png', 1.0);
            link.click();
            
        } catch (error) {
            console.error('Error al generar la imagen:', error);
            alert('Hubo un error al generar la imagen. Por favor intenta de nuevo.');
        } finally {
            setDescargando(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="lg"
            aria-labelledby="birthday-modal"
            centered
            className="birthday-modal"
        >
            <Modal.Body className="birthday-modal-body" ref={modalContentRef}>
                {/* Confetti decorativo - visible en captura */}
                <div className="confetti-container" style={{ zIndex: 1 }}>
                    {[...Array(60)].map((_, i) => (
                        <div 
                            key={i} 
                            className={`confetti confetti-${i % 6}`}
                            style={{
                                left: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 3}s`,
                                animationDuration: `${3 + Math.random() * 2}s`,
                                position: 'absolute',
                                top: `${Math.random() * 100}%`
                            }}
                        ></div>
                    ))}
                </div>
                
                {/* Contenido principal */}
                <div className="birthday-content">
                    {/* Fecha de cumpleaños en calendario decorativo */}
                    <div className="birthday-date-badge">
                        <div className="date-badge-header">{mes.toUpperCase()}</div>
                        <div className="date-badge-day">{dia}</div>
                    </div>

                    {/* Título Happy Birthday */}
                    <h1 className="birthday-title">
                        <span className="birthday-title-happy">Feliz</span>
                        <span className="birthday-title-birthday">Cumpleaños</span>
                    </h1>
                    
                    {/* Globos decorativos superiores */}
                    <div className="birthday-balloons-top">
                        <span className="balloon balloon-green-left">🎈</span>
                        <span className="balloon balloon-purple">🎈</span>
                        <span className="balloon balloon-yellow">🎈</span>
                    </div>

                    {/* Pastel de cumpleaños */}
                    <div className="birthday-cake-container">
                        <div className="cake">
                            {/* Velas */}
                            <div className="candles">
                                <div className="candle">
                                    <div className="flame"></div>
                                </div>
                                <div className="candle">
                                    <div className="flame"></div>
                                </div>
                                <div className="candle">
                                    <div className="flame"></div>
                                </div>
                            </div>
                            {/* Capas del pastel */}
                            <div className="cake-layer cake-top"></div>
                            <div className="cake-layer cake-middle"></div>
                            <div className="cake-layer cake-bottom"></div>
                            <div className="cake-base"></div>
                        </div>
                    </div>

                    {/* Globos decorativos inferiores */}
                    <div className="birthday-balloons-bottom">
                        <span className="balloon balloon-red">🎈</span>
                        <span className="balloon balloon-green-right">🎈</span>
                        <span className="balloon balloon-blue">🎈</span>
                    </div>
                    
                    {/* Nombre del cumpleañero */}
                    <h3 className="birthday-name">
                        {nombresCumpleaneros}
                    </h3>

                    {/* Fecha completa decorativa */}
                    <div className="birthday-full-date">
                        <span className="date-icon">🎂</span>
                        <span className="date-text">{fechaFormateada}</span>
                        <span className="date-icon">🎂</span>
                    </div>
                    
                    {/* Mensaje motivador */}
                    <p className="birthday-message">
                        {fraseAleatoria}
                    </p>
                    
                    {/* Firma */}
                    <div className="birthday-signature">
                        <em>— Equipo Akasia Software Development</em>
                    </div>
                </div>
            </Modal.Body>
            
            <Modal.Footer className="birthday-modal-footer">
                <Button 
                    variant="success" 
                    onClick={descargarImagen} 
                    className="birthday-download-btn"
                    disabled={descargando}
                >
                    {descargando ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Generando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon="download" className="me-2" />
                            Descargar Imagen
                        </>
                    )}
                </Button>
                <Button 
                    variant="light" 
                    onClick={handleClose} 
                    className="birthday-close-btn"
                    disabled={descargando}
                >
                    <FontAwesomeIcon icon="heart" className="me-2" />
                    ¡Gracias!
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

BirthdayModal.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    nombresCumpleaneros: PropTypes.string.isRequired
};

export default BirthdayModal;
